import { createEntityId } from '@/core/EntityId';
import type { FloraCatalog } from '@/flora/FloraCatalog';
import type { FloraInstance } from '@/flora/FloraInstance';
import type { PlantGroup } from '@/editor/GroupManager';
import {
  defaultEnvironmentState,
  WEATHER_PRESETS,
  type EnvironmentState,
  type WeatherPreset,
} from '@/environment/EnvironmentState';
import {
  createSiteObject,
  normalizeMass,
  syncExtents,
  type BuildingMass,
  type SiteObject,
} from '@/site/SiteObject';
import { groundSamplePoints } from '@/site/SiteObjectGeometry';
import {
  isValidRing,
  normalizeRing,
  pointInRing,
  distanceToRing,
  ringArea,
  ringBounds,
  ringCentroid,
  toWorldPoint,
  type Point2,
  type Ring,
} from '@/site/SiteFootprint';
import { createGenerationParams } from '@/terrain/TerrainTemplates';
import { generateHeightfield } from '@/terrain/TerrainGenerator';
import {
  sampleHeightWorld,
  type Heightfield,
  type TerrainGenerationParams,
  type TerrainProjectState,
} from '@/terrain/TerrainTypes';
import {
  SurfaceMap,
  compositeLayerWeight,
  dominantLayerAt,
  findSurfaceLayer,
  isPlantableSurface,
  serializeSurfaceMap,
  surfaceCoverage,
  surfacePalette,
  texelToWorld,
  type SurfaceCoverage,
} from '@/terrain/TerrainSurface';
import { PROJECT_SCHEMA_VERSION, deriveAliasGroups, type LandscapeProject } from '@/project/Project';
import { mulberry32, targetPositions, tryPlace, type PlacementContext } from './PlantingPlacement';
import { pipelineSemanticTags } from './PipelineSemantics';
import type { ReferenceTag } from './ReferenceTag';
import type {
  MeasuredGround,
  PipelineArtifacts,
  PlanStructure,
  SemanticMap,
  SurfaceArtifact,
  TerrainArtifact,
} from './SitePipeline';

/**
 * Assemble the five stage artifacts into a `LandscapeProject`.
 *
 * The division of labour is the one this whole feature runs on, applied stage by stage: a model
 * supplies the judgements that require having looked at a photograph, and this module does every
 * piece of arithmetic those judgements imply. Concretely, it does four things no stage could have
 * done for itself:
 *
 * 1. **Sculpts** the heightfield — a measured lattice resampled straight onto the grid when the
 *    terrain stage carries one, otherwise a procedural base with the stage's control points blended
 *    in by inverse-distance weighting. Baked either way as authoritative heights.
 * 2. **Paints** the ground — each surface polygon rasterized into the splat map with a real feathered
 *    edge, on the terrain that was just sculpted.
 * 3. **Builds** the structures — world-space polygons converted into objects with their own local
 *    frames, masses resolved, and nested surfaces lifted onto their host's datum.
 * 4. **Plants** — masses expanded by the shared placement engine, now refusing not only buildings
 *    but the blacktop and water stage 3 painted, which is the rule the interactive tool enforces.
 *
 * Fully deterministic: seeded RNG, no clock, no `Math.random`. Re-running with the same artifacts
 * and seed reproduces the site exactly, which is what makes "re-roll the layout" a free local button
 * rather than a second set of API calls.
 *
 * It never throws on a merely questionable artifact. A stage will hand back a polygon that runs off
 * the site or a species this build does not have; those are dropped and **reported**, because a site
 * that quietly loses a third of its planting is worse than one that says what it lost.
 */

/**
 * Something the compiler could not honour, named so it can be shown rather than swallowed.
 *
 * Shared with the magic wand, which compiles the same planting shapes against a site that already
 * exists and reports the same way.
 */
export interface CompileWarning {
  planting?: string;
  message: string;
}

/** One planting mass, as asked for and as it actually landed. */
export interface CompiledMass {
  id: string;
  name: string;
  speciesId: string;
  requested: number;
  placed: number;
}

export interface PipelineCompileOptions {
  catalog: FloraCatalog;
  /** Varies the planting layout without re-running any stage. */
  seed?: number;
  /** Injected so a compiled project has a stable identity and timestamps in tests. */
  projectId?: string;
  now?: Date;
  /** Site name, when the user has typed one. */
  name?: string;
  /**
   * Tags placed on the reference photograph before the run, so the designer's own statements land on
   * the site they were written about. See {@link pipelineSemanticTags} for which of them carry.
   */
  referenceTags?: readonly ReferenceTag[];
  /** Vertical exaggeration. Defaults to {@link TOPOGRAPHY_EXAGGERATION}; 1 reads the stage literally. */
  topographyExaggeration?: number;
}

/**
 * How far the compiled ground is pushed past what the terrain stage actually read.
 *
 * Not a correction to the stage — its readings are usually about right, and the arithmetic that
 * turns them into a heightfield is faithful. It is a correction to *how they land*. A real garden's
 * grade is read at walking pace over minutes, with your own body as the scale; the same 0.4 m fall
 * seen from a 3D camera across a 30 m site is a few pixels of shading, and the site reads as the
 * flat plate the stage was careful to say it was not. Doubling the relief about the datum makes the
 * landform legible at the distance it is actually viewed from, which is the point of modelling it.
 *
 * Applied to every route to the ground — procedural amplitude, control points, and a measured DEM
 * alike — because relief that changed depending on whether a survey happened to be available would
 * be a worse inconsistency than the exaggeration itself. The stage artifacts are left untouched, so
 * the inspector still shows what the model said rather than what the compiler did with it.
 */
export const TOPOGRAPHY_EXAGGERATION = 2;

/** What the compiler did, stage by stage — the last piece of collateral the inspector shows. */
export interface PipelineCompileReport {
  plantCount: number;
  speciesCount: number;
  siteObjectCount: number;
  buildingCount: number;
  massCount: number;
  groupCount: number;
  /** Semantic tags carried onto the site — the designer's marks plus stage 1's identifications. */
  tagCount: number;
  /** Control points that actually moved the ground. */
  sculptedPoints: number;
  /** Where the heightfield came from — the one thing that decides whether to trust its detail. */
  groundSource: GroundSource;
  /** For `measured`: the fraction of the site that had a reading, `0..1`. Zero otherwise. */
  measuredCoverage: number;
  /** Metres of relief between the lowest and highest baked vertex. */
  reliefMeters: number;
  /** Fraction of the site each painted material ended up covering, `0..1`. */
  coverage: SurfaceCoverage;
  paintedRegions: number;
  masses: CompiledMass[];
  warnings: CompileWarning[];
}

export interface PipelineCompileResult {
  project: LandscapeProject;
  report: PipelineCompileReport;
}

/** Grid resolution of the compiled terrain. */
const TERRAIN_SEGMENTS = 144;

export function compilePipeline(
  artifacts: Partial<PipelineArtifacts>,
  options: PipelineCompileOptions,
): PipelineCompileResult {
  const warnings: CompileWarning[] = [];
  const seed = Math.max(1, Math.trunc(options.seed ?? 1));
  const rand = mulberry32(seed);

  const segment = artifacts.segment;
  if (!segment) {
    throw new Error('The semantic identification stage has not produced a result yet.');
  }

  /* -- 1. Terrain ----------------------------------------------------------------- */

  // Exaggerated once, at the boundary, so the generation parameters stored on the project describe
  // the heights that were actually baked. Everything downstream reads the exaggerated artifact and
  // needs to know nothing about the factor.
  const exaggerated = exaggerateTerrain(artifacts.terrain, options.topographyExaggeration);
  const generation = terrainParams(segment, exaggerated, seed);
  const ground = groundFor(generation, exaggerated, warnings);
  const sculpted = ground.field;

  /* -- 2. Surfaces ---------------------------------------------------------------- */

  // Sized for the palette in force, which by now includes anything the surfaces stage had forged.
  const surface = SurfaceMap.forSite(
    generation.widthMeters,
    generation.lengthMeters,
    undefined,
    surfacePalette.pages,
  );
  const paintedRegions = paintSurfaces(surface, artifacts.surfaces, warnings);

  /* -- 3. Structures -------------------------------------------------------------- */

  const siteObjects = buildStructures(artifacts.structures, sculpted, warnings);

  /* -- 4. Planting ---------------------------------------------------------------- */

  const context: PlacementContext = {
    field: sculpted,
    halfX: generation.widthMeters / 2,
    halfZ: generation.lengthMeters / 2,
    siteObjects,
    placed: [],
    rand,
    // The paint tool refuses to plant on blacktop and open water; a generated site is held to the
    // same rule, or the reconstruction produces something the user could not have drawn by hand.
    plantable: (x, z) => isPlantableSurface(dominantLayerAt(surface, x, z)),
  };

  const { instances, groups, masses } = plantMasses(
    artifacts.flora?.plantings ?? [],
    context,
    options.catalog,
    warnings,
  );

  /* -- 5. Assemble ---------------------------------------------------------------- */

  const now = (options.now ?? new Date()).toISOString();
  const terrain: TerrainProjectState = {
    generation,
    units: 'imperial',
    // Authoritative: the control points are not reproducible from the generation parameters, so a
    // reload must read the heights rather than re-derive them.
    bakedHeights: Array.from(sculpted.heights, (height) => round(height, 4)),
  };
  if (!surface.isEmpty()) terrain.surface = serializeSurfaceMap(surface);

  const project: LandscapeProject = {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    id: options.projectId ?? createEntityId('project-'),
    name: options.name?.trim() || 'Site from reference',
    description: describe(artifacts),
    createdAt: now,
    updatedAt: now,
    units: 'imperial',
    terrain,
    // The forged materials go into the document with the site that needed them, or reopening it
    // would find paint referring to materials that no longer exist.
    ...(surfacePalette.custom().length > 0 ? { surfacePalette: surfacePalette.toState() } : {}),
    environment: environmentFor(segment),
    floraInstances: instances,
    siteObjects,
    faunaInstances: [],
    groups,
    aliasGroups: deriveAliasGroups(instances),
    cameraViews: [],
    // Everything the reconstruction learned about *what things are*, which is the one class of
    // knowledge no later pass can recover from the geometry. See `PipelineSemantics.ts`.
    semanticTags: pipelineSemanticTags({
      segment,
      structures: artifacts.structures?.structures ?? [],
      builtObjectIds: new Set(siteObjects.map((object) => object.id)),
      ...(options.referenceTags ? { referenceTags: options.referenceTags } : {}),
      ...(options.now ? { now: options.now } : {}),
    }),
    floraCatalogVersion: options.catalog.version,
  };

  return {
    project,
    report: {
      plantCount: instances.length,
      speciesCount: new Set(instances.map((instance) => instance.floraDefinitionId)).size,
      siteObjectCount: siteObjects.length,
      buildingCount: siteObjects.filter((object) => object.kind === 'building').length,
      massCount: siteObjects.reduce((total, object) => total + (object.masses?.length ?? 0), 0),
      groupCount: groups.length,
      tagCount: project.semanticTags.length,
      sculptedPoints: artifacts.terrain?.controlPoints.length ?? 0,
      groundSource: ground.source,
      measuredCoverage: round(ground.coverage, 4),
      reliefMeters: round(relief(sculpted), 3),
      coverage: surfaceCoverage(surface),
      paintedRegions,
      masses,
      warnings,
    },
  };
}

/* --------------------------------- 1. Terrain --------------------------------- */

/**
 * The terrain artifact with its relief scaled about the datum — see {@link TOPOGRAPHY_EXAGGERATION}.
 *
 * Returns a new artifact rather than mutating: the runner holds the stage's own output, the
 * inspector shows it, and a re-run diffs against it. What the compiler does with those numbers is
 * the compiler's business and must not be written back into the record of what the model said.
 *
 * Every measured lattice is median-centred on the way in (`gridToMeasuredGround`), and control
 * points are elevations relative to the site datum by definition, so zero is the fixed point in
 * both cases and a plain multiply is the whole operation. The result is re-clamped into the ranges
 * `parseTerrainArtifact` guarantees, so an exaggerated artifact is still a valid one.
 */
export function exaggerateTerrain(
  terrain: TerrainArtifact | undefined,
  factor = TOPOGRAPHY_EXAGGERATION,
): TerrainArtifact | undefined {
  if (!terrain) return undefined;
  const scale = Number.isFinite(factor) ? Math.max(0, factor) : TOPOGRAPHY_EXAGGERATION;
  if (scale === 1) return terrain;

  const exaggerated: TerrainArtifact = {
    ...terrain,
    elevationAmplitude: clamp(terrain.elevationAmplitude * scale, 0, 20),
    controlPoints: terrain.controlPoints.map((point) => ({
      ...point,
      elevationMeters: clamp(point.elevationMeters * scale, -50, 50),
    })),
  };
  if (terrain.measured) {
    exaggerated.measured = {
      ...terrain.measured,
      // A hole stays a hole: it is an admission that nothing was measured there, and multiplying it
      // by anything would turn it into a reading of zero.
      heights: terrain.measured.heights.map((height) => (height === null ? null : height * scale)),
    };
  }
  return exaggerated;
}

function terrainParams(
  segment: SemanticMap,
  terrain: TerrainArtifact | undefined,
  seed: number,
): TerrainGenerationParams {
  return createGenerationParams('blank-custom', {
    widthMeters: clamp(segment.site.widthMeters, 5, 200),
    lengthMeters: clamp(segment.site.lengthMeters, 5, 200),
    seed,
    elevationAmplitude: clamp(terrain?.elevationAmplitude ?? 0.4, 0, 20),
    // Measured ground leaves nothing for noise to do, and a stored roughness the baked heights do
    // not exhibit would mislead anyone who later re-generated from these parameters.
    roughness: terrain?.measured ? 0 : clamp(terrain?.roughness ?? 0.25, 0, 1),
    segments: TERRAIN_SEGMENTS,
  });
}

/** Which of the two terrain paths produced the heightfield. */
export type GroundSource = 'measured' | 'control-points' | 'procedural';

interface CompiledGround {
  field: Heightfield;
  source: GroundSource;
  /** Fraction of the site with a real reading. Only meaningful for `measured`. */
  coverage: number;
}

/**
 * The heightfield, by whichever route the evidence supports.
 *
 * Two genuinely different procedures, and keeping them apart is the point. A **measured** lattice is
 * resampled onto the compiler's grid and baked: no procedural base, no inverse-distance blending,
 * nothing between the DEM and the terrain but a bilinear lookup. Anything else falls to the old
 * path, where a couple of dozen readings are eased into noise precisely because they are too sparse
 * to stand on their own.
 *
 * Control points are *not* re-applied over measured ground. They describe the same landform the
 * lattice already carries, so blending them in would smooth the survey back toward its own summary —
 * which is the exact loss this branch exists to stop.
 */
function groundFor(
  generation: TerrainGenerationParams,
  terrain: TerrainArtifact | undefined,
  warnings: CompileWarning[],
): CompiledGround {
  const base = generateHeightfield(generation);
  if (terrain?.measured) {
    return bakeMeasuredGround(base, terrain.measured, warnings);
  }
  return {
    field: sculptField(base, terrain, warnings),
    source: (terrain?.controlPoints.length ?? 0) > 0 ? 'control-points' : 'procedural',
    coverage: 0,
  };
}

/**
 * Resample a measured lattice onto the compiler's grid.
 *
 * Bilinear, one lookup per terrain vertex, every vertex overwritten — the procedural field arrives
 * only as a correctly-shaped container. Off the lattice's edge the value is clamped to it, which is
 * the honest extension of a survey that does not quite reach the site boundary; a vertex whose
 * neighbours are all holes is left at the datum. Both are counted, because "the ground looks wrong"
 * and "a third of the ground was never measured" are the same bug reported at different resolutions.
 */
export function bakeMeasuredGround(
  field: Heightfield,
  ground: MeasuredGround,
  warnings: CompileWarning[],
): CompiledGround {
  const heights = Float32Array.from(field.heights);
  let outside = 0;
  let holes = 0;

  for (let row = 0; row < field.rows; row += 1) {
    for (let col = 0; col < field.cols; col += 1) {
      const x = (col / Math.max(1, field.cols - 1) - 0.5) * field.widthMeters;
      const z = (row / Math.max(1, field.rows - 1) - 0.5) * field.lengthMeters;

      const gc = (x - ground.originX) / ground.spacingXMeters;
      const gr = (z - ground.originZ) / ground.spacingZMeters;
      if (gc < -0.5 || gr < -0.5 || gc > ground.cols - 0.5 || gr > ground.rows - 0.5) outside += 1;

      const height = sampleMeasured(ground, gc, gr);
      if (height === null) holes += 1;
      heights[row * field.cols + col] = height ?? 0;
    }
  }

  const total = field.cols * field.rows;
  const covered = (total - outside - holes) / total;

  if (outside / total > 0.02) {
    warnings.push({
      message:
        `The measured ground covers ${Math.round((1 - outside / total) * 100)} % of the site; the ` +
        'rest was extended flat from its nearest edge. Widen the survey, or narrow the site extent.',
    });
  }
  if (holes > 0) {
    warnings.push({
      message:
        `${Math.round((holes / total) * 100)} % of the site had no reading in ${ground.label} and ` +
        'was left at the site datum.',
    });
  }

  return { field: { ...field, heights }, source: 'measured', coverage: Math.max(0, covered) };
}

/** One bilinear sample of the measured lattice, in lattice coordinates, edge-clamped. */
function sampleMeasured(ground: MeasuredGround, gc: number, gr: number): number | null {
  const col = Math.floor(gc);
  const row = Math.floor(gr);
  const fx = gc - col;
  const fz = gr - row;

  let weightSum = 0;
  let valueSum = 0;
  for (const [dc, dr, weight] of [
    [0, 0, (1 - fx) * (1 - fz)],
    [1, 0, fx * (1 - fz)],
    [0, 1, (1 - fx) * fz],
    [1, 1, fx * fz],
  ] as const) {
    if (weight <= 0) continue;
    const c = Math.min(ground.cols - 1, Math.max(0, col + dc));
    const r = Math.min(ground.rows - 1, Math.max(0, row + dr));
    const height = ground.heights[r * ground.cols + c];
    if (height === null || height === undefined || !Number.isFinite(height)) continue;
    weightSum += weight;
    valueSum += weight * height;
  }
  return weightSum <= 0 ? null : valueSum / weightSum;
}

/**
 * Blend the terrain stage's control points into the procedural base.
 *
 * Inverse-distance weighting with a smooth compact kernel: each point's influence falls to zero at
 * its own radius, so a reading about the bank behind the shed cannot tilt the front lawn. Where the
 * combined influence is partial the base noise still shows through, which keeps a sculpted site from
 * reading as a set of smooth bowls stamped onto a flat plane.
 *
 * Returns a **new** heightfield rather than mutating, so the unmodified procedural field stays
 * available for comparison.
 */
export function sculptField(
  field: Heightfield,
  terrain: TerrainArtifact | undefined,
  warnings: CompileWarning[],
): Heightfield {
  const heights = Float32Array.from(field.heights);
  const points = (terrain?.controlPoints ?? []).filter((point) => {
    const inside =
      Math.abs(point.x) <= field.widthMeters / 2 + point.radiusMeters &&
      Math.abs(point.z) <= field.lengthMeters / 2 + point.radiusMeters;
    if (!inside) {
      warnings.push({
        message: `A terrain control point at (${round(point.x, 1)}, ${round(point.z, 1)}) lies off the site and was ignored.`,
      });
    }
    return inside;
  });

  if (points.length === 0) {
    return { ...field, heights };
  }

  for (let row = 0; row < field.rows; row += 1) {
    for (let col = 0; col < field.cols; col += 1) {
      const index = row * field.cols + col;
      const x = (col / Math.max(1, field.cols - 1) - 0.5) * field.widthMeters;
      const z = (row / Math.max(1, field.rows - 1) - 0.5) * field.lengthMeters;

      let weightSum = 0;
      let targetSum = 0;
      for (const point of points) {
        const distance = Math.hypot(x - point.x, z - point.z);
        if (distance >= point.radiusMeters) continue;
        // Smoothstep-squared falloff: flat at the centre, tangent at the rim, so adjacent points
        // merge into one landform instead of showing a seam.
        const t = 1 - distance / point.radiusMeters;
        const weight = t * t * (3 - 2 * t);
        weightSum += weight;
        targetSum += weight * point.elevationMeters;
      }
      if (weightSum <= 0) continue;

      const blend = Math.min(1, weightSum);
      const target = targetSum / weightSum;
      heights[index] = (heights[index] ?? 0) * (1 - blend) + target * blend;
    }
  }

  return { ...field, heights };
}

function relief(field: Heightfield): number {
  let min = Infinity;
  let max = -Infinity;
  for (const height of field.heights) {
    if (!Number.isFinite(height)) continue;
    min = Math.min(min, height);
    max = Math.max(max, height);
  }
  return Number.isFinite(min) && Number.isFinite(max) ? max - min : 0;
}

/* --------------------------------- 2. Surfaces --------------------------------- */

/**
 * Rasterize the surface polygons into the splat map, softest edge last.
 *
 * Regions are painted in the order the stage listed them, using the same `compositeLayerWeight`
 * operator the brush uses — so a generated site's paint is indistinguishable from hand-painted
 * ground and stays a partition of unity.
 *
 * The edge is a true signed-distance ramp centred on the polygon boundary. Feathering inward only
 * would shrink every region by half its feather, which on a 3 m path is the difference between a
 * path and a line.
 */
export function paintSurfaces(
  map: SurfaceMap,
  surfaces: SurfaceArtifact | undefined,
  warnings: CompileWarning[],
): number {
  if (!surfaces) return 0;
  let painted = 0;

  for (const region of surfaces.regions) {
    const ring: Ring = region.polygon.map(([x, z]) => ({ x, z }));
    if (!isValidRing(ring)) {
      warnings.push({ message: `Surface “${region.id}” had no usable outline and was skipped.` });
      continue;
    }
    const normalized = normalizeRing(ring);
    // A material that is not on the palette is one the stage asked for and the forge failed to make.
    // Named rather than substituted: painting it as gravel would hide the failure under a surface
    // that looks deliberate, and bare lawn with a warning is the honest reading.
    const layer = findSurfaceLayer(region.material);
    if (!layer) {
      warnings.push({
        message: `Surface “${region.id}” wanted the material “${region.material}”, which could not be made. That area was left unpainted.`,
      });
      continue;
    }
    const feather = Math.max(1e-3, region.featherMeters);

    // Only visit texels the region can reach: its bounds grown by the feather.
    const bounds = ringBounds(normalized);
    const half = { x: map.widthMeters / 2, z: map.lengthMeters / 2 };
    const minCol = Math.max(0, Math.floor(((bounds.minX - feather + half.x) / map.widthMeters) * map.width));
    const maxCol = Math.min(map.width - 1, Math.ceil(((bounds.maxX + feather + half.x) / map.widthMeters) * map.width));
    const minRow = Math.max(0, Math.floor(((bounds.minZ - feather + half.z) / map.lengthMeters) * map.height));
    const maxRow = Math.min(map.height - 1, Math.ceil(((bounds.maxZ + feather + half.z) / map.lengthMeters) * map.height));
    if (maxCol < minCol || maxRow < minRow) {
      warnings.push({ message: `Surface “${region.id}” fell entirely outside the site and was skipped.` });
      continue;
    }

    let touched = 0;
    for (let row = minRow; row <= maxRow; row += 1) {
      for (let col = minCol; col <= maxCol; col += 1) {
        const world = texelToWorld(map, col, row);
        const inside = pointInRing(normalized, world.x, world.z);
        const distance = distanceToRing(normalized, world.x, world.z);
        const signed = inside ? distance : -distance;
        const weight = clamp(0.5 + signed / feather, 0, 1);
        if (weight <= 0) continue;
        compositeLayerWeight(map, row * map.width + col, layer.channel, weight);
        touched += 1;
      }
    }

    if (touched === 0) {
      warnings.push({
        message: `Surface “${region.id}” (${layer.name}) covered no ground — its outline is smaller than one texel.`,
      });
      continue;
    }
    painted += 1;
  }

  if (painted > 0) map.touch();
  return painted;
}

/* -------------------------------- 3. Structures -------------------------------- */

/**
 * Turn world-space structure plans into site objects with their own local frames.
 *
 * The conversion is the reason the pipeline asks for polygons instead of centre-plus-extents-plus-
 * angle: a polygon already carries its own orientation, so the object takes `rotationY = 0` and
 * stores the outline relative to its centroid. There is no rotation for a stage to get the sign of
 * backwards, and `SiteObject.syncExtents` still derives readable width and depth from the shape.
 */
export function buildStructures(
  artifact: { structures: PlanStructure[] } | undefined,
  field: Heightfield,
  warnings: CompileWarning[],
): SiteObject[] {
  const objects: SiteObject[] = [];
  if (!artifact) return objects;

  for (const plan of artifact.structures) {
    const object = buildStructure(plan, warnings);
    if (object) objects.push(object);
  }

  // `sitsOn` resolution. Each object is placed at the highest ground under its own footprint, so a
  // pool nested in a larger terrace lands on a lower datum and is swallowed by the deck it belongs
  // to. Lifting it onto the host's datum is the fix, and it can only be done once terrain is known.
  const byId = new Map(objects.map((object) => [object.id, object]));
  for (const plan of artifact.structures) {
    if (!plan.sitsOn) continue;
    const object = byId.get(plan.id);
    const host = byId.get(plan.sitsOn);
    if (!object || !host) {
      warnings.push({ message: `“${plan.name}” sits on an unknown object “${plan.sitsOn}”.` });
      continue;
    }
    object.position.y = round(highestUnder(host, field) - highestUnder(object, field) + 0.05);
  }

  return objects;
}

function buildStructure(plan: PlanStructure, warnings: CompileWarning[]): SiteObject | null {
  const shared = {
    heightMeters: plan.heightMeters ?? (plan.kind === 'building' ? 2.6 : 0),
    roof: plan.roof ?? 'gable',
    roofRiseMeters: plan.roofRiseMeters ?? 1.2,
    wallColor: plan.wallColor ?? (plan.kind === 'building' ? '#b9b2a4' : '#b0a293'),
    roofColor: plan.roofColor ?? '#6d6f73',
    paving: plan.paving ?? 'flagstone',
    clearanceMeters: plan.clearanceMeters ?? (plan.kind === 'building' ? 0.8 : 0),
    locked: false,
    hidden: false,
  } as const;

  // A building described as masses: origin at the area-weighted centroid of them all.
  if (plan.kind === 'building' && plan.masses && plan.masses.length > 0) {
    const rings = plan.masses
      .map((mass) => normalizeRing(mass.polygon.map(([x, z]) => ({ x, z }))))
      .filter((ring) => isValidRing(ring));
    if (rings.length === 0) {
      warnings.push({ message: `“${plan.name}” had no usable mass outlines and was skipped.` });
      return null;
    }

    const origin = areaWeightedCentroid(rings);
    const masses: BuildingMass[] = [];
    for (const [index, mass] of plan.masses.entries()) {
      const ring = rings[index];
      if (!ring) continue;
      masses.push(
        normalizeMass({
          footprint: ring.map((point) => ({ x: point.x - origin.x, z: point.z - origin.z })),
          heightMeters: mass.heightMeters,
          roof: mass.roof,
          roofRiseMeters: mass.roofRiseMeters,
          ...(mass.name !== undefined ? { name: mass.name } : {}),
          ...(mass.baseMeters !== undefined ? { baseMeters: mass.baseMeters } : {}),
          ...(mass.ridgeCrosswise === true ? { ridgeCrosswise: true } : {}),
        }),
      );
    }

    return finish(plan, {
      position: { x: round(origin.x), y: 0, z: round(origin.z) },
      rotationY: 0,
      masses,
      ...shared,
    });
  }

  // A single polygon — the common case for paving and for a simple building.
  if (plan.polygon && plan.polygon.length >= 3) {
    const ring = normalizeRing(plan.polygon.map(([x, z]) => ({ x, z })));
    if (!isValidRing(ring)) {
      warnings.push({ message: `“${plan.name}” had a degenerate outline and was skipped.` });
      return null;
    }
    const origin = ringCentroid(ring);
    return finish(plan, {
      position: { x: round(origin.x), y: 0, z: round(origin.z) },
      rotationY: 0,
      footprint: ring.map((point) => ({ x: point.x - origin.x, z: point.z - origin.z })),
      ...shared,
    });
  }

  // Rectangle fallback, for a shed that genuinely is a box.
  if (plan.widthMeters === undefined || plan.depthMeters === undefined) {
    warnings.push({
      message: `“${plan.name}” gave neither an outline nor a width and depth, so it was skipped.`,
    });
    return null;
  }
  return finish(plan, {
    position: { x: plan.x ?? 0, y: 0, z: plan.z ?? 0 },
    rotationY: ((plan.rotationDegrees ?? 0) * Math.PI) / 180,
    widthMeters: plan.widthMeters,
    depthMeters: plan.depthMeters,
    ...shared,
  });
}

function finish(plan: PlanStructure, overrides: Partial<SiteObject>): SiteObject {
  const object = createSiteObject({
    kind: plan.kind,
    name: plan.name,
    overrides: { ...overrides, id: plan.id },
  });
  return syncExtents(object);
}

/** Centroid of several rings, weighted by area so a large range outweighs a small porch. */
function areaWeightedCentroid(rings: readonly Ring[]): Point2 {
  let x = 0;
  let z = 0;
  let total = 0;
  for (const ring of rings) {
    const area = ringArea(ring);
    const centroid = ringCentroid(ring);
    x += centroid.x * area;
    z += centroid.z * area;
    total += area;
  }
  if (total <= 0) return { x: 0, z: 0 };
  return { x: x / total, z: z / total };
}

/** Highest ground under a footprint — every vertex plus each ring's centre. */
function highestUnder(object: SiteObject, field: Heightfield): number {
  let highest = -Infinity;
  for (const local of groundSamplePoints(object)) {
    const world = toWorldPoint(local, object.position, object.rotationY);
    highest = Math.max(highest, sampleHeightWorld(field, world.x, world.z));
  }
  return Number.isFinite(highest) ? highest : 0;
}

/* --------------------------------- 4. Planting --------------------------------- */

function plantMasses(
  plantings: PipelineArtifacts['flora']['plantings'],
  context: PlacementContext,
  catalog: FloraCatalog,
  warnings: CompileWarning[],
): { instances: FloraInstance[]; groups: PlantGroup[]; masses: CompiledMass[] } {
  const groupsByName = new Map<string, PlantGroup>();
  const instances: FloraInstance[] = [];
  const masses: CompiledMass[] = [];

  for (const planting of plantings) {
    if (!catalog.has(planting.speciesId)) {
      warnings.push({
        planting: planting.name,
        message: `“${planting.speciesId}” is not in this build's plant catalog — ${planting.name} was skipped.`,
      });
      masses.push({
        id: planting.id,
        name: planting.name,
        speciesId: planting.speciesId,
        requested: 0,
        placed: 0,
      });
      continue;
    }

    const footprintRadius = catalog.get(planting.speciesId).placement.footprintRadiusMeters;
    const { targets, retryable } = targetPositions(planting, context);
    if (targets.length === 0) {
      warnings.push({
        planting: planting.name,
        message: `${planting.name} described no regions, drifts, or line, so nothing was planted.`,
      });
    }

    let group: PlantGroup | undefined;
    if (planting.group) {
      group = groupsByName.get(planting.group);
      if (!group) {
        group = { id: createEntityId('group-'), name: planting.group, memberIds: [] };
        groupsByName.set(planting.group, group);
      }
    }
    const aliasGroupId = planting.linked === true ? createEntityId('alias-') : undefined;

    let placed = 0;
    for (const target of targets) {
      const instance = tryPlace(planting, target, retryable, context, footprintRadius, {
        ...(group ? { groupId: group.id } : {}),
        ...(aliasGroupId ? { aliasGroupId } : {}),
      });
      if (!instance) continue;
      instances.push(instance);
      group?.memberIds.push(instance.id);
      placed += 1;
    }

    masses.push({
      id: planting.id,
      name: planting.name,
      speciesId: planting.speciesId,
      requested: targets.length,
      placed,
    });
    if (targets.length > 0 && placed < targets.length * 0.6) {
      warnings.push({
        planting: planting.name,
        message:
          `${planting.name}: only ${placed} of ${targets.length} fitted — the area is crowded, ` +
          'off the site, blocked by a building, or on ground that refuses planting.',
      });
    }
  }

  return {
    instances,
    groups: [...groupsByName.values()].filter((group) => group.memberIds.length > 0),
    masses,
  };
}

/* --------------------------------- 5. Assemble --------------------------------- */

function environmentFor(segment: SemanticMap): EnvironmentState {
  const base = defaultEnvironmentState();
  const observations = segment.observations;
  return {
    ...base,
    time: { hour: observations.hour, minute: observations.minute },
    // Calendar mode always: simple mode snaps to four anchors and discards the date the model read
    // off the foliage, which is the single most informative thing it recovered.
    date: {
      mode: 'calendar',
      season: seasonLabel(observations.dayOfYear),
      dayOfYear: observations.dayOfYear,
    },
    weather: WEATHER_PRESETS.includes(observations.weather as WeatherPreset)
      ? (observations.weather as WeatherPreset)
      : 'clear',
    latitudeDegrees: observations.latitudeDegrees,
  };
}

function seasonLabel(dayOfYear: number): EnvironmentState['date']['season'] {
  if (dayOfYear < 80 || dayOfYear >= 355) return 'winter';
  if (dayOfYear < 172) return 'spring';
  if (dayOfYear < 266) return 'summer';
  return 'autumn';
}

/**
 * The project description, assembled from every stage's own notes.
 *
 * Each stage is asked to record what it inferred rather than observed; collecting those into the
 * document is what keeps the reconstruction an auditable claim about a real place rather than an
 * anonymous result. The list of unmodelled things is included for the same reason — the absence of a
 * fence should read as a decision, not an oversight.
 */
function describe(artifacts: Partial<PipelineArtifacts>): string {
  const parts: string[] = [];
  const segment = artifacts.segment;

  if (segment) {
    parts.push(
      `Scale anchor: ${segment.scale.description} — ${segment.scale.meters} m spanning ` +
        `${Math.round(segment.scale.imageFraction * 100)}% of the image width. ` +
        `Orientation: ${segment.site.orientation}.`,
    );
    if (segment.notes) parts.push(segment.notes);
    if (segment.unmodelled.length > 0) {
      parts.push(`Not modelled: ${segment.unmodelled.join('; ')}.`);
    }
  }

  for (const [label, notes] of [
    ['Terrain', artifacts.terrain?.notes],
    ['Surfaces', artifacts.surfaces?.notes],
    ['Planting', artifacts.flora?.notes],
    ['Structures', artifacts.structures?.notes],
  ] as const) {
    if (notes) parts.push(`${label}: ${notes}`);
  }

  return parts.filter((part) => part.trim().length > 0).join('\n\n');
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round(value: number, places = 3): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}
